<template>
  <v-dialog v-model="visible" persistent max-width="638">
    <v-card v-if="visible" class="v-dialog--custom">
      <v-card-title class="headline pa-4">
        <span class="v-dialog--custom__title my-2">{{ $t(`${namespace}.setWebCallPermissionTitle`) }}</span>
        <v-btn class="v-dialog--custom__close mr-2" fab flat color="gray-6" :disabled="isLoading" @click="close">
          <v-icon dark>close</v-icon>
        </v-btn>
      </v-card-title>

      <form class="form--custom" novalidate @submit.prevent="batchUpdate">
        <v-card-text class="px-4 pt-0">
          <p class="pb-2">{{ $t(`${namespace}.setWebCallPermissionMsg`) }}</p>
          <p><strong>{{ $t(`${namespace}.setWebCallPermissionStep1`) }}</strong></p>
          <v-radio-group
            v-model="radioGroupEmployees"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t('Common.employees'))"
            :data-vv-name="$t('Common.employees')"
            required
          >
            <v-radio :label="$t(`${namespace}.allEmployees`)" value="allEmployees"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesByName`)" value="perName"></v-radio>
            <v-radio :label="$t(`${namespace}.selectEmployeesById`)" value="perId"></v-radio>
          </v-radio-group>

          <AutoComplete
            v-if="radioGroupEmployees === 'perName'"
            kind="employee"
            :data="employeeIds"
            :companyId="company.id"
            :placeholder="$t('Components.ApplyToAllDialog.searchEmployees')"
            :isValid="radioGroupEmployees === 'perName' && isEmployeeValid"
            :required="radioGroupEmployees === 'perName'"
            multiple
            @input="updateEmployee"
          />

          <v-text-field
            v-if="radioGroupEmployees === 'perId'"
            v-model="employeesIdsField"
            flat
            outline
            solo
            color="accent"
            clearable
            :name="$t(`${namespace}.fieldIdsName`)"
            :placeholder="$t(`${namespace}.fieldIdsPlaceholder`)"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t(`${namespace}.fieldIdsName`))"
            :data-vv-name="$t(`${namespace}.fieldIdsName`)"
            required
            @blur="stringToArray"
          />

          <p><strong>{{ $t(`${namespace}.setWebCallPermissionStep2`) }}</strong></p>
          <v-radio-group
            v-model="radioGroupPermission"
            v-validate="{ required: true }"
            :error-messages="errors.collect($t(`${namespace}.fieldPermissionName`))"
            :data-vv-name="$t(`${namespace}.fieldPermissionName`)"
            required
            @change="getWebCallPermission"
          >
            <v-radio
              v-for="(item, index) in $t(`${namespace}.setWebCallPermissionItems`)"
              :key="index"
              :label="item.label"
              :value="item.value"
            />
          </v-radio-group>
        </v-card-text>

        <v-divider />

        <v-card-actions class="pa-4">
          <v-spacer />
          <a
            v-if="!isLoading"
            href="#"
            class="text--normal accent--text mr-4"
            color="accent"
            flat
            round
            @click.prevent="close"
          >
            {{ $t('Common.cancel') }}
          </a>
          <v-btn
            type="submit"
            :disabled="isLoading"
            :loading="isLoading"
            round
            depressed
            color="accent"
          >
            {{ $t(`${namespace}.updatePermissions`) }}
          </v-btn>
        </v-card-actions>
      </form>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isArray } from 'utils/functions'
import AutoComplete from 'components/AutoComplete'

export default {
  name: 'SetWebCallPermissionBulk',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    })
  },
  components: {
    AutoComplete
  },
  data () {
    return {
      namespace: 'Components.ApplyToAllDialog',
      isLoading: false,
      radioGroupEmployees: null,
      radioGroupPermission: null,

      // custom validation
      isEmployeeValid: true,

      // fields
      employeeIds: [],
      employeesIdsField: null,
      thirdPartyWebCallPermission: null,
      webCallPermission: null
    }
  },
  methods: {
    ...mapActions({
      successAlert: 'snackbarAlert/success',
      errorAlert: 'snackbarAlert/error'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    resetEmployeeOptions () {
      this.employeeIds = []
      this.employeesIdsField = null
    },

    resetAllFields () {
      this.resetEmployeeOptions()
      this.isEmployeeValid = true
      this.radioGroupEmployees = null
      this.radioGroupPermission = null
    },

    stringToArray () {
      if (!this.employeesIdsField) return
      const arr = this.employeesIdsField.replace(/[[A-zÀ-ú]-\s._]/g, '').split(',')
      this.employeeIds = arr.map(item => Number(item))
    },

    close () {
      this.$emit('close')
    },

    updateEmployee (val) {
      if (!this.isValidArray(val)) {
        this.employeeIds = []
        this.radioGroupEmployees !== 'allEmployees' && (this.isEmployeeValid = false)
        return
      }

      this.employeeIds = val
      this.radioGroupEmployees !== 'allEmployees' && (this.isEmployeeValid = true)
    },

    getWebCallPermission (val) {
      this.thirdPartyWebCallPermission = false
      this.webCallPermission = false

      if (val === 'onlyForEmployee') {
        this.webCallPermission = true
        return
      }

      if (val === 'onlyForThirdParties') {
        this.thirdPartyWebCallPermission = true
        return
      }

      this.thirdPartyWebCallPermission = true
      this.webCallPermission = true
    },

    async batchUpdate () {
      if (this.radioGroupEmployees !== 'allEmployees' && !this.employeeIds.length) {
        this.isEmployeeValid = false
      }

      try {
        const isValid = await this.$validator.validateAll()

        if (this.radioGroupEmployees !== 'allEmployees' && !this.employeeIds.length) return

        if (isValid) {
          this.isLoading = true

          // WORKAROUND
          let ids = null

          if (this.radioGroupEmployees === 'perName') {
            const employeeIds = this.employeeIds.map(item => item.id)
            ids = this.employeeIds.length ? employeeIds : null
          } else {
            ids = this.employeeIds
          }

          await this.$apiService.batchUpdate(
            this.company.id,
            { budget: null },
            { budgetDate: null },
            { budgetPeriod: 'monthly' },
            { credit: null },
            { useBudget: null },
            { useRideCredit: null },
            { categoryIds: null },
            { supervisorId: null },
            { thirdPartyWebCallPermission: this.thirdPartyWebCallPermission },
            { webCallPermission: this.webCallPermission },
            { ids },
            { v2Permission: true }
          )

          this.successAlert(this.$t(`${this.namespace}.setWebCallPermissionSuccess`))
          this.resetAllFields()
          this.close()
          this.isLoading = false
        }
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.error`))
        this.isLoading = false
      }
    }
  },
  watch: {
    radioGroupEmployees () {
      this.resetEmployeeOptions()
    },

    employeesIdsField (val) {
      if (!val) {
        this.resetEmployeeOptions()
      }
    },

    visible (val) {
      // fix validation error when close dialog
      this.$validator._paused = !val

      this.resetAllFields()

      this.isLoading = false
      this.radioGroupEmployees = null
      this.radioGroupPermission = null

      // fields
      this.employeeIds = []
      this.employeesIdsField = null
      this.thirdPartyWebCallPermission = null
      this.webCallPermission = null

      val && this.$ga.page('/dialog/colaboradores/permissoes-para-chamada-web-em-massa')
    }
  }
}
</script>
